import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useSnackbar } from "notistack";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDBadge from "components/MDBadge";

import EntityTable, { StatusBadge, RowActionMenu } from "components/EntityTable";
import ConfirmDialog from "components/ConfirmDialog";
import SidePanel from "components/SidePanel";
import AuthorForm from "components/content/AuthorForm";
import usePaginatedResource from "hooks/usePaginatedResource";
import useDebounce from "hooks/useDebounce";
import { listAuthorsPaged, deleteAuthor } from "api/endpoints/authors";

// Batch 2.2 — Authors management screen (server-paginated).
function AuthorsListPage() {
  const { websiteId } = useOutletContext();
  const { enqueueSnackbar } = useSnackbar();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);

  const [panel, setPanel] = useState({ open: false, author: null });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchAuthors = useCallback(
    (pageParams) => {
      if (!websiteId) return { items: [], total: 0 };
      return listAuthorsPaged(websiteId, { ...pageParams, search: debouncedSearch });
    },
    [websiteId, debouncedSearch]
  );

  const { rows, total, loading, page, pageSize, setPage, setPageSize, refresh } =
    usePaginatedResource(fetchAuthors);

  // A new search term restarts at the first page.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, setPage]);

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await deleteAuthor(websiteId, confirmDeleteId);
      enqueueSnackbar("Author deleted", { variant: "success" });
      refresh();
    } catch {
      enqueueSnackbar("Failed to delete author", { variant: "error" });
    } finally {
      setDeleting(false);
      setConfirmDeleteId(null);
    }
  };

  const openCreate = () => setPanel({ open: true, author: null });
  const openEdit = (author) => setPanel({ open: true, author });
  const closePanel = () => setPanel({ open: false, author: null });

  const columns = [
    { field: "display_name", headerName: "Name", flex: 1, minWidth: 160 },
    {
      field: "email",
      headerName: "Email",
      flex: 1.2,
      minWidth: 200,
      renderCell: ({ value }) => value || "—",
    },
    {
      field: "is_global",
      headerName: "Scope",
      flex: 0.7,
      minWidth: 120,
      renderCell: ({ value }) => (
        <MDBadge
          badgeContent={value ? "Global" : "Website"}
          color={value ? "info" : "secondary"}
          variant="gradient"
          size="sm"
          container
        />
      ),
    },
    {
      field: "is_active",
      headerName: "Status",
      flex: 0.6,
      minWidth: 110,
      renderCell: ({ value }) => <StatusBadge active={value} />,
    },
    {
      field: "actions",
      headerName: "",
      width: 64,
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <RowActionMenu
          actions={[
            { label: "Edit", icon: "edit", onClick: () => openEdit(row) },
            {
              label: "Delete",
              icon: "delete",
              color: "error",
              onClick: () => setConfirmDeleteId(row.author_id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <MDBox>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        mb={3}
      >
        <MDTypography variant="h5" fontWeight="bold">
          Authors
        </MDTypography>
        <MDButton variant="gradient" color="info" startIcon={<Icon>add</Icon>} onClick={openCreate}>
          New Author
        </MDButton>
      </MDBox>

      <Card>
        <MDBox p={3}>
          <MDBox mb={2} maxWidth={320}>
            <MDInput
              fullWidth
              placeholder="Search by name or email"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{ endAdornment: <Icon fontSize="small">search</Icon> }}
            />
          </MDBox>
          <EntityTable
            rows={rows}
            columns={columns}
            getRowId={(row) => row.author_id}
            loading={loading}
            paginationMode="server"
            rowCount={total}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onRowClick={({ row }) => openEdit(row)}
          />
        </MDBox>
      </Card>

      <SidePanel
        open={panel.open}
        onClose={closePanel}
        title={panel.author ? "Edit Author" : "New Author"}
        subtitle="Manage the people credited on this website's content"
        width={760}
      >
        {panel.open && (
          <AuthorForm
            websiteId={websiteId}
            author={panel.author}
            onSaved={() => {
              closePanel();
              refresh();
            }}
          />
        )}
      </SidePanel>

      <ConfirmDialog
        open={confirmDeleteId !== null}
        title="Delete Author"
        message="Are you sure you want to delete this author?"
        confirmText="Delete"
        loading={deleting}
        onConfirm={confirmDelete}
        onClose={() => setConfirmDeleteId(null)}
      />
    </MDBox>
  );
}

export default AuthorsListPage;
